/**
 * Custom PrimeVue Theme Preset
 * Extends Aura theme with dynamic brand colors from tenant configuration.
 *
 * @see https://primevue.org/theming/styled/
 */

import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'
import { generateColors, type TenantConfigLike } from './colors'
import { getContrastingTextColor } from './colorUtils'

export function createFranchiseTheme(tenantConfig: TenantConfigLike | null = null) {
  const colors = generateColors(tenantConfig)
  // Text on a tenant color is picked by contrast: white on #ffc107 is unreadable
  const onPrimary = getContrastingTextColor(colors.primary['500'])
  const onPrimaryDark = getContrastingTextColor(colors.primary['400'])
  const onSecondary = getContrastingTextColor(colors.secondary['500'])
  const onSecondaryDark = getContrastingTextColor(colors.secondary['400'])

  return definePreset(Aura, {
    semantic: {
      primary: colors.primary,

      colorScheme: {
        light: {
          primary: {
            color: colors.primary['500'],
            inverseColor: onPrimary,
            hoverColor: colors.primary['600'],
            activeColor: colors.primary['700']
          },

          secondary: {
            color: colors.secondary['500'],
            inverseColor: onSecondary,
            hoverColor: colors.secondary['600'],
            activeColor: colors.secondary['700']
          },

          highlight: {
            background: colors.primary['500'],
            focusBackground: colors.primary['600'],
            color: onPrimary,
            focusColor: onPrimary
          }
        },

        dark: {
          primary: {
            color: colors.primary['400'],
            inverseColor: onPrimaryDark,
            hoverColor: colors.primary['300'],
            activeColor: colors.primary['200']
          },

          secondary: {
            color: colors.secondary['400'],
            inverseColor: onSecondaryDark,
            hoverColor: colors.secondary['300'],
            activeColor: colors.secondary['200']
          },

          highlight: {
            background: colors.primary['400'],
            focusBackground: colors.primary['300'],
            color: onPrimaryDark,
            focusColor: onPrimaryDark
          }
        }
      }
    },

    components: {
      button: {
        colorScheme: {
          light: {
            primary: {
              background: colors.primary['500'],
              hoverBackground: colors.primary['600'],
              activeBackground: colors.primary['700'],
              borderColor: colors.primary['500'],
              hoverBorderColor: colors.primary['600'],
              activeBorderColor: colors.primary['700'],
              color: onPrimary,
              hoverColor: onPrimary,
              activeColor: onPrimary,
              focusRing: {
                color: colors.primary['500'],
                shadow: `0 0 0 0.2rem ${colors.primary['200']}`
              }
            },

            secondary: {
              background: colors.secondary['500'],
              hoverBackground: colors.secondary['600'],
              activeBackground: colors.secondary['700'],
              borderColor: colors.secondary['500'],
              hoverBorderColor: colors.secondary['600'],
              activeBorderColor: colors.secondary['700'],
              color: onSecondary,
              hoverColor: onSecondary,
              activeColor: onSecondary,
              focusRing: {
                color: colors.secondary['500'],
                shadow: `0 0 0 0.2rem ${colors.secondary['200']}`
              }
            }
          } as any
        }
      },

      dialog: {
        colorScheme: {
          light: {
            header: {
              background: colors.secondary['500'],
              borderColor: colors.secondary['600'],
              color: '#ffffff',
              padding: '1rem 1.5rem'
            } as any,
            content: {
              background: '#ffffff',
              borderColor: colors.secondary['100'],
              color: '#495057',
              padding: '0'
            } as any,
            footer: {
              background: '#f8f9fa',
              borderColor: '#dee2e6',
              color: '#495057',
              padding: '1rem 1.5rem'
            } as any
          }
        }
      },

      inputtext: {
        colorScheme: {
          light: {
            root: {
              focusBorderColor: colors.primary['500']
            }
          }
        }
      },

      dropdown: {
        colorScheme: {
          light: {
            root: {
              focusBorderColor: colors.primary['500']
            }
          }
        }
      },

      calendar: {
        colorScheme: {
          light: {
            root: {
              focusBorderColor: colors.primary['500']
            }
          }
        }
      }
    }
  })
}

export const FranchiseTheme = createFranchiseTheme(null)

export default FranchiseTheme
